import React, { useState } from "react";
import { t } from "../i18n";
import ExportButtons from "./ExportButtons";
import ViolationsList from "./ViolationsList";
import { CaretDown, WarningCircle } from "@phosphor-icons/react";

function scoreColor(score) {
  if (score >= 85) return "text-emerald-700 border-emerald-600 bg-emerald-50";
  if (score >= 70) return "text-amber-700 border-amber-600 bg-amber-50";
  if (score >= 50) return "text-orange-700 border-orange-600 bg-orange-50";
  return "text-red-700 border-red-600 bg-red-50";
}

function PageRow({ rep, lang }) {
  const [open, setOpen] = useState(false);
  const sc = scoreColor(rep.compliance_score);
  return (
    <div className="border-t border-neutral-200" data-testid={`batch-page-row-${rep.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50 ma-trans"
        data-testid={`batch-page-toggle-${rep.id}`}
      >
        <span
          className={`inline-flex items-center justify-center w-12 h-7 text-xs font-mono border ${sc}`}
        >
          {rep.compliance_score}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-mono truncate text-neutral-900" title={rep.source}>
            {rep.source}
          </div>
          <div className="text-[10px] font-mono text-neutral-500">
            {(rep.stats || {}).total || 0} issues · {rep.page_type}
          </div>
        </div>
        <ExportButtons lang={lang} kind="report" id={rep.id} />
        <CaretDown
          size={14}
          className={`text-neutral-500 ma-trans ml-2 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-5 pt-1 ma-in space-y-3">
          {rep.summary && (
            <div className="border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-800">
              {rep.summary}
            </div>
          )}
          <ViolationsList violations={rep.violations} lang={lang} />
        </div>
      )}
    </div>
  );
}

export default function BatchResults({ batch, lang }) {
  if (!batch) return null;

  return (
    <div className="space-y-5" data-testid="batch-results">
      {/* Summary card */}
      <div className="border border-neutral-900 bg-white">
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-900 bg-neutral-900 text-white">
          <div className="text-xs uppercase tracking-[0.2em] font-mono">
            {t(lang, "batch_results")}
          </div>
          <ExportButtons lang={lang} kind="batch" id={batch.id} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0">
          <Stat label={t(lang, "average_score")} value={`${batch.average_score}/100`} testid="batch-stat-avg" />
          <Stat label={t(lang, "total_urls")} value={batch.total_urls} testid="batch-stat-total" />
          <Stat label={t(lang, "succeeded")} value={batch.succeeded} testid="batch-stat-ok" tone="text-emerald-700" />
          <Stat label={t(lang, "failed")} value={batch.failed} testid="batch-stat-failed" tone="text-red-700" />
          <Stat label={t(lang, "source")} value={batch.source} testid="batch-stat-source" mono last />
        </div>
      </div>

      {/* Pages */}
      <div className="border border-neutral-900 bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-900">
          <div className="text-xs uppercase tracking-[0.2em] font-mono">
            {t(lang, "pages")}
          </div>
          <span className="text-[10px] font-mono text-neutral-500">
            {batch.reports?.length || 0}
          </span>
        </div>
        {(batch.reports || []).map((rep) => (
          <PageRow key={rep.id} rep={rep} lang={lang} />
        ))}
      </div>

      {/* Errors */}
      {batch.errors && batch.errors.length > 0 && (
        <div className="border border-red-400 bg-red-50">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-red-300">
            <WarningCircle size={14} className="text-red-700" weight="fill" />
            <div className="text-xs uppercase tracking-[0.2em] font-mono text-red-800">
              {t(lang, "failed_urls")} ({batch.errors.length})
            </div>
          </div>
          <ul className="p-4 space-y-1.5 text-xs font-mono text-red-900">
            {batch.errors.map((e, i) => (
              <li key={i} data-testid={`batch-error-${i}`} className="break-all">
                <span className="font-semibold">{e.url}</span> — {e.error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone, testid, mono, last }) {
  return (
    <div
      className={`px-4 py-3 ${last ? "" : "border-r border-neutral-200"} border-b md:border-b-0 border-neutral-200`}
      data-testid={testid}
    >
      <div className="text-[9px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-1">
        {label}
      </div>
      <div className={`font-display font-semibold text-lg truncate ${tone || "text-neutral-900"} ${mono ? "font-mono text-xs" : ""}`} title={String(value)}>
        {value}
      </div>
    </div>
  );
}
